import React, {useEffect, useMemo, useState} from "react";
import {Badge, Button, Form, Modal, Spinner} from "react-bootstrap";
import {ToastContainer} from "react-toastify";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {useNavigate} from "react-router-dom";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import {buildAuthSession} from "../../../../helpers/AuthSession";
import {defaultAppRoute} from "../../../../helpers/DefaultAppRoute";
import UserRolesCard from "./UserRolesCard";
import PermissionMatrix from "../permissions/PermissionMatrix";
import {hasPermission, PERMISSIONS as P} from "../../../../auth/permissions";
import {normalizeRoles, ROLES} from "../../../../auth/roles";
import "../../../../css/Permissions.css";
import "../../../../css/UserManagement.css";
import "../../../../css/ResourceForms.css";

const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;

const emptyUser = {
  id: null,
  login: "",
  password: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  userRoleDTOS: [],
  userRoles: [],
  enabled: true,
  linkedEmployee: false,
  employeeId: null,
  employeeDisplayName: "",
  departmentName: ""
};

const ROLE_LABELS = {
  ADMIN: "Administrator",
  MANAGER: "Kierownik",
  HR: "Kadry / HR",
  EMPLOYEE: "Pracownik",
  USER: "Użytkownik",
  GUEST: "Gość",
  MINION: "Pracownik pomocniczy"
};

const normalizeRoleName = value => String(value || "").trim().toUpperCase().replace(/^ROLE_/, "");

const Users = () => {
  const axiosPrivate = useAxiosPrivate();
  const {auth, setAuth} = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesLoadError, setRolesLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(emptyUser);
  const [backupItem, setBackupItem] = useState(emptyUser);
  const [itemChanged, setItemChanged] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [permissionOverrides, setPermissionOverrides] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const canManageUsers = hasPermission(auth?.permissions, P.USERS_MANAGE);
  const canManagePermissions = hasPermission(auth?.permissions, P.PERMISSIONS_MANAGE);
  const canImpersonate = hasPermission(auth?.permissions, P.IMPERSONATION_START);

  const currentFormState = useMemo(() => ({
    formAddingDataMode: !currentItem.id,
    formSaveButtonDisabled: false
  }), [currentItem.id]);

  const setCurrentFormState = () => {};
  const state = {
    currentItem,
    setCurrentItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    currentFormState,
    setCurrentFormState
  };

  const loadRoles = async () => {
    setRolesLoading(true);
    setRolesLoadError("");
    try {
      const response = await axiosPrivate.get("/admin/role");
      const loadedRoles = Array.isArray(response.data) ? response.data : [];
      setRoles(loadedRoles);
      if (loadedRoles.length === 0) {
        setRolesLoadError("Backend zwrócił pustą listę ról. Sprawdź definicje ról w Administracja → Definicje ról.");
      }
      return loadedRoles;
    } catch (error) {
      setRoles([]);
      setRolesLoadError("Nie udało się pobrać definicji ról z /admin/role.");
      return [];
    } finally {
      setRolesLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const usersResponse = await axiosPrivate.get("/user");
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (error) {
      toastError("Nie udało się pobrać użytkowników.");
    } finally {
      setLoading(false);
    }
    await loadRoles();
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter(user => {
      const userRoles = user.userRoleDTOS || [];
      const roleNames = userRoles.map(role => role.name).join(" ");
      const matchesRole = roleFilter === "ALL" || userRoles.some(role => normalizeRoleName(role.name) === roleFilter);
      const matchesSearch = !query || [
        user.login,
        user.employeeDisplayName,
        user.departmentName,
        user.firstName,
        user.lastName,
        roleNames
      ].filter(Boolean).join(" ").toLowerCase().includes(query);
      return matchesRole && matchesSearch;
    });
  }, [users, search, roleFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const firstVisible = filteredUsers.length ? (page - 1) * pageSize + 1 : 0;
  const lastVisible = Math.min(page * pageSize, filteredUsers.length);

  const openNew = async () => {
    if (!canManageUsers) return;
    if (!roles.length) await loadRoles();
    const next = {...emptyUser};
    setCurrentItem(next);
    setBackupItem(next);
    setPassword("");
    setPasswordConfirmation("");
    setShowModal(true);
  };

  const openEdit = async (user) => {
    try {
      if (!roles.length) await loadRoles();
      const [response, catalogResponse, overridesResponse, effectiveResponse] = await Promise.all([
        axiosPrivate.get(`/user/${user.id}`),
        axiosPrivate.get("/admin/permissions/catalog"),
        axiosPrivate.get(`/admin/permissions/users/${user.id}`),
        axiosPrivate.get(`/admin/permissions/users/${user.id}/effective`)
      ]);
      setPermissionCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
      setPermissionOverrides(Array.isArray(overridesResponse.data) ? overridesResponse.data : []);
      setEffectivePermissions(Array.isArray(effectiveResponse.data) ? effectiveResponse.data : []);
      const item = {
        ...emptyUser,
        ...response.data,
        userRoles: response.data.userRoleDTOS || []
      };
      setCurrentItem(item);
      setBackupItem(item);
      setPassword("");
      setPasswordConfirmation("");
      setShowModal(true);
    } catch (error) {
      toastError("Nie udało się pobrać danych użytkownika.");
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setCurrentItem(emptyUser);
    setBackupItem(emptyUser);
    setPassword("");
    setPasswordConfirmation("");
  };

  const changeField = (field, value) => {
    setCurrentItem(item => ({...item, [field]: value}));
    setItemChanged(valueChanged => !valueChanged);
  };

  const errorMessage = error => {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    return data?.message || data?.error || "Nie udało się zapisać użytkownika.";
  };

  const saveUser = async () => {
    const isNew = !currentItem.id;
    if (!canManageUsers && !canManagePermissions) return;
    if (isNew && !canManageUsers) return;
    if (canManageUsers && (!currentItem.login || currentItem.login.trim().length < 4)) {
      toastError("Login musi mieć co najmniej 4 znaki.");
      return;
    }

    const selectedRoles = currentItem.userRoleDTOS || [];
    if (canManageUsers && !selectedRoles.length) {
      toastError("Użytkownik musi mieć przynajmniej jedną rolę.");
      return;
    }

    if (canManageUsers && isNew && !password) {
      toastError("Hasło nowego użytkownika jest wymagane.");
      return;
    }

    if (canManageUsers && password && !PWD_REGEX.test(password)) {
      toastError("Hasło musi mieć 8–24 znaki, małą i wielką literę, cyfrę oraz znak ! @ # $ %. ");
      return;
    }

    if (canManageUsers && password !== passwordConfirmation) {
      toastError("Podane hasła są różne.");
      return;
    }

    const payload = {
      id: currentItem.id,
      login: currentItem.login.trim(),
      password: password || null,
      firstName: currentItem.firstName || null,
      lastName: currentItem.lastName || null,
      phoneNumber: currentItem.phoneNumber || null,
      userRoleDTOS: selectedRoles.map(role => ({id: role.id, name: role.name})),
      enabled: !!currentItem.enabled
    };

    setSaving(true);
    try {
      if (canManageUsers) {
        if (isNew) {
          await axiosPrivate.post("/user", payload);
          toastSuccess("Utworzono konto użytkownika.");
        } else {
          await axiosPrivate.put(`/user/${currentItem.id}`, payload);
        }
      }
      if (!isNew && canManagePermissions) {
        await axiosPrivate.put(`/admin/permissions/users/${currentItem.id}`, permissionOverrides);
      }
      if (!isNew) toastSuccess("Zapisano ustawienia użytkownika.");
      setShowModal(false);
      setCurrentItem(emptyUser);
      setBackupItem(emptyUser);
      setPassword("");
      setPasswordConfirmation("");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };


  const startImpersonation = async user => {
    if (auth?.impersonated || !user?.id || !user.enabled || String(user.id) === String(auth?.id)) return;
    setImpersonatingUserId(user.id);
    try {
      const response = await axiosPrivate.post(`/api/impersonation/start/${user.id}`);
      const session = buildAuthSession(response.data);
      setAuth(session);
      const targetRoles = normalizeRoles(session.roles);
      const employeeSelfService = targetRoles.includes(ROLES.EMPLOYEE) || targetRoles.includes(ROLES.MINION);
      navigate(employeeSelfService ? defaultAppRoute(session) : "/app/account", {replace: true});
    } catch (error) {
      const data = error?.response?.data;
      const message = typeof data === "string"
        ? data
        : data?.message || data?.error_message || data?.error || "Nie udało się uruchomić podglądu użytkownika.";
      toastError(message);
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const roleLabel = role => ROLE_LABELS[normalizeRoleName(role.name)] || role.name;

  return (
    <div id="layoutSidenav_content" className="user-management-page resource-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Użytkownicy aplikacji"/>
      <div className="container-fluid px-4 py-3">
        <div className="user-management-intro">
          <div>
            <h5>Konta logujące się do Qstaff</h5>
            <p>Tu zarządzasz aktywnością kont i rolami. Pracownik oraz użytkownik to dwa różne rekordy połączone loginem. Konto tworzone z kartoteki pracownika jest tutaj widoczne i zarządzane z tego samego źródła ról.</p>
          </div>
          {canManageUsers && <Button onClick={openNew}>Dodaj konto techniczne</Button>}
        </div>

        <div className="card shadow-sm mb-5 resource-list-card resource-standard-list-card user-management-list-card">
          <div className="resource-list-toolbar user-management-toolbar">
            <Form.Control
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Szukaj po loginie, pracowniku, dziale lub roli…"
            />
            <Form.Select value={roleFilter} onChange={event => setRoleFilter(event.target.value)} aria-label="Filtr roli">
              <option value="ALL">Wszystkie role</option>
              {roles.map(role => {
                const value = normalizeRoleName(role.name);
                return <option key={role.id} value={value}>{roleLabel(role)}</option>;
              })}
            </Form.Select>
            <Form.Select value={pageSize} onChange={event => setPageSize(Number(event.target.value))} aria-label="Liczba wierszy na stronie">
              <option value={50}>50 wierszy</option>
              <option value={100}>100 wierszy</option>
              <option value={500}>500 wierszy</option>
            </Form.Select>
            <span>Widocznych: {filteredUsers.length} / {users.length}</span>
          </div>

          <div className="user-management-table-wrap resource-list-table-scroll">
            {loading ? (
              <div className="user-management-loading"><Spinner animation="border" size="sm"/> Pobieranie danych…</div>
            ) : (
              <table className="table align-middle resource-list-table user-list-table user-management-table">
              <thead>
              <tr>
                <th>Login</th>
                <th>Powiązany pracownik</th>
                <th>Dział</th>
                <th>Konto</th>
                <th>Role</th>
                <th>Akcje</th>
              </tr>
              </thead>
              <tbody>
              {pagedUsers.map(user => (
                <tr key={user.id}>
                  <td><strong>{user.login}</strong></td>
                  <td>
                    {user.linkedEmployee ? (
                      <div className="user-linked-employee">
                        <strong>{user.employeeDisplayName || `${user.firstName || ""} ${user.lastName || ""}`.trim()}</strong>
                        <small>Pracownik #{user.employeeId}</small>
                      </div>
                    ) : <span className="text-muted">konto techniczne / administracyjne</span>}
                  </td>
                  <td>{user.departmentName || "—"}</td>
                  <td>
                    <span className={`user-status ${user.enabled ? "active" : "disabled"}`}>
                      {user.enabled ? "Aktywne" : "Wyłączone"}
                    </span>
                  </td>
                  <td>
                    <div className="user-role-badges">
                      {(user.userRoleDTOS || []).map(role => (
                        <Badge key={role.id} className="user-role-badge">{roleLabel(role)}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="user-management-actions">
                      {canImpersonate && <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={!!auth?.impersonated || !user.enabled || String(user.id) === String(auth?.id) || impersonatingUserId !== null}
                        title={auth?.impersonated
                          ? "Najpierw wróć do konta administratora"
                          : !user.enabled
                          ? "Nie można zalogować się jako użytkownik z wyłączonym kontem"
                          : String(user.id) === String(auth?.id)
                            ? "To jest Twoje bieżące konto"
                            : "Uruchom aplikację z uprawnieniami tego użytkownika"}
                        onClick={() => startImpersonation(user)}
                      >
                        {impersonatingUserId === user.id ? <><Spinner animation="border" size="sm"/> Przełączanie…</> : "Zaloguj jako"}
                      </Button>}
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(user)}>{canManageUsers ? "Edytuj" : canManagePermissions ? "Uprawnienia" : "Podgląd"}</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredUsers.length && (
                <tr><td colSpan="6" className="text-center text-muted py-4">Brak użytkowników spełniających kryteria.</td></tr>
              )}
              </tbody>
            </table>
            )}
          </div>
          <div className="user-management-pagination">
            <span>Wiersze {firstVisible}–{lastVisible} z {filteredUsers.length}</span>
            <div>
              <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>Poprzednia</Button>
              <strong>Strona {page} z {totalPages}</strong>
              <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>Następna</Button>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} size="xl" backdrop="static" className="user-management-modal" dialogClassName="user-management-modal-dialog">
        <Modal.Header closeButton>
          <Modal.Title>{currentItem.id ? `Użytkownik: ${currentItem.login}` : "Nowe konto użytkownika"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentItem.linkedEmployee && (
            <div className="user-management-link-note">
              <strong>To konto jest powiązane z pracownikiem:</strong> {currentItem.employeeDisplayName}
              {currentItem.departmentName ? ` · ${currentItem.departmentName}` : ""}.
              Login jest wspólnym identyfikatorem i nie można go tutaj zmienić.
            </div>
          )}

          {!currentItem.id && (
            <div className="user-management-link-note neutral">
              Konta pracowników najwygodniej tworzyć z kartoteki pracownika. Ten formularz służy przede wszystkim do kont technicznych lub administracyjnych. Jeśli podasz login istniejącego pracownika, backend automatycznie powiąże konto z jego kartoteką.
            </div>
          )}

          <div className="user-management-form-grid">
            <Form.Group>
              <Form.Label>Login</Form.Label>
              <Form.Control value={currentItem.login || ""} disabled={!!currentItem.id || !canManageUsers} onChange={event => changeField("login", event.target.value)}/>
            </Form.Group>

            {!currentItem.linkedEmployee && (
              <>
                <Form.Group>
                  <Form.Label>Imię</Form.Label>
                  <Form.Control value={currentItem.firstName || ""} disabled={!canManageUsers} onChange={event => changeField("firstName", event.target.value)}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nazwisko</Form.Label>
                  <Form.Control value={currentItem.lastName || ""} disabled={!canManageUsers} onChange={event => changeField("lastName", event.target.value)}/>
                </Form.Group>
              </>
            )}

            <Form.Group className="user-management-enabled">
              <Form.Check
                type="switch"
                id="user-enabled"
                label="Konto aktywne"
                checked={!!currentItem.enabled}
                disabled={!canManageUsers}
                onChange={event => changeField("enabled", event.target.checked)}
              />
              <small>Wyłączone konto nie może się ponownie zalogować ani odświeżyć sesji.</small>
            </Form.Group>
          </div>

          <div className="mt-3">
            {rolesLoading && (
              <div className="user-role-load-state"><Spinner animation="border" size="sm"/> Pobieranie ról…</div>
            )}
            {!rolesLoading && rolesLoadError && (
              <div className="user-role-load-error">
                <strong>Nie można wyświetlić ról.</strong>
                <span>{rolesLoadError}</span>
                <Button size="sm" variant="outline-secondary" onClick={loadRoles}>Spróbuj ponownie</Button>
              </div>
            )}
            {!rolesLoading && !rolesLoadError && (
              <UserRolesCard
                state={state}
                rolesList={roles}
                disabled={!canManageUsers}
              />
            )}
          </div>

          {currentItem.id && permissionCatalog.length > 0 && (
            <div className="mt-4">
              <h6>Indywidualne uprawnienia użytkownika</h6>
              <p className="text-muted">Domyślnie użytkownik dziedziczy uprawnienia z ról. Tutaj możesz nadać dodatkowe prawo albo odebrać prawo wynikające z roli.</p>
              <PermissionMatrix catalog={permissionCatalog} grants={permissionOverrides} onChange={setPermissionOverrides} mode="user" disabled={!canManagePermissions}/>
              <div className="mt-3"><strong>Uprawnienia wynikowe: </strong>{effectivePermissions.length ? effectivePermissions.map(item => item.code).join(", ") : "brak"}</div>
            </div>
          )}

          <div className="user-password-box">
            <strong>{currentItem.id ? "Reset hasła" : "Hasło"}</strong>
            <span>{currentItem.id ? "Pozostaw oba pola puste, jeśli hasło ma pozostać bez zmian." : "Hasło jest wymagane przy tworzeniu konta."}</span>
            <div className="user-management-form-grid two-columns">
              <Form.Group>
                <Form.Label>Nowe hasło</Form.Label>
                <Form.Control type="password" value={password} disabled={!canManageUsers} onChange={event => setPassword(event.target.value)}/>
              </Form.Group>
              <Form.Group>
                <Form.Label>Powtórz hasło</Form.Label>
                <Form.Control type="password" value={passwordConfirmation} disabled={!canManageUsers} onChange={event => setPasswordConfirmation(event.target.value)}/>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>Anuluj</Button>
          {(canManageUsers || canManagePermissions) && <Button onClick={saveUser} disabled={saving}>{saving ? "Zapisywanie…" : "Zapisz"}</Button>}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Users;

